import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faAngleRight } from "@fortawesome/pro-solid-svg-icons/faAngleRight";
import { faArrowLeftFromBracket } from "@fortawesome/pro-solid-svg-icons/faArrowLeftFromBracket";
import { faLifeRing } from "@fortawesome/pro-solid-svg-icons/faLifeRing";
import { faUsersCog } from "@fortawesome/pro-solid-svg-icons/faUsersCog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuProps } from "antd/es/menu";
import { defineMessages, useIntl } from "react-intl";
import { useLocation } from "react-router";
import { Link, useHistory } from "react-router-dom";

import { Avatar, Dropdown } from "@mapmycustomers/ui";

import featuresCtaSrc from "@app/assets/features/cta.png";
import configService from "@app/config/ConfigService";
import Environment from "@app/enum/Environment";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import { logout } from "@app/store/auth/actions";
import { getMe, getOrganizationAddOnServices, getPlan, getPlans } from "@app/store/iam";
import { isOwner } from "@app/store/iam/util";
import { RootState } from "@app/store/rootReducer";
import { navbarUserMenuClick } from "@app/util/analytic/navbarAnalytics";
import { getLegacyLogoutAppUrl } from "@app/util/appUrl";
import getSellableBands from "@app/util/features/getSellableBands";
import getCurrentSubscriptionPlan from "@app/util/plans/getCurrentSubscriptionPlan";

import styles from "./UserMenu.module.scss";
import UserProfileInfo from "./UserProfileInfo";

const messages = defineMessages({
  featuresDescription: {
    id: "navbar.userMenu.features.description",
    defaultMessage: "See new features that can help your team succeed.",
    description: "Description of the features block",
  },
  featuresTitle: {
    id: "navbar.userMenu.features.title",
    defaultMessage: "Discover New Features!",
    description: "Title of the features block",
  },
  logoutMenuItem: {
    id: "navbar.userMenu.logout",
    defaultMessage: "Logout",
    description: "Title of logout option",
  },
  subscriptionMenuItem: {
    id: "navbar.userMenu.manageSubscription",
    defaultMessage: "Manage Your Subscription",
    description: "Title of manage subscription option",
  },
  supportMenuItem: {
    id: "navbar.userMenu.support",
    defaultMessage: "Support",
    description: "Title of support option",
  },
});

const featuresPagePath = `${Path.SETTINGS}/${SettingPath.ORGANIZATION_FEATURES}`;

const UserMenu = ({
  addOnServices,
  currentPlan,
  logout,
  me,
  plans,
}: ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const history = useHistory();
  const { pathname } = useLocation();
  const baseOldAppUrl = configService.getBaseOldAppUrl();

  // Logout wrapped in useCallback to avoid click event getting into its arguments
  const handleLogoutClick = useCallback(
    () => logout(getLegacyLogoutAppUrl(baseOldAppUrl)),
    [baseOldAppUrl, logout]
  );

  const handleFeaturesClick = useCallback(() => history.push(featuresPagePath), [history]);

  // Asks exactly what the Features page asks, so the entry never leads to a page with no bands.
  const sellableBands = useMemo(
    () => getSellableBands(addOnServices, getCurrentSubscriptionPlan(currentPlan?.name), plans),
    [addOnServices, currentPlan, plans]
  );

  const showFeaturesBlock =
    isOwner(me) && pathname !== featuresPagePath && sellableBands.length > 0;

  const menu: MenuProps = useMemo(() => {
    return {
      className: styles.userMenuItem,
      items: [
        {
          className: styles.userProfileInfo,
          key: "userProfileInfo",
          label: <UserProfileInfo user={me} />,
        },
        { type: "divider" },
        ...(showFeaturesBlock
          ? [
              {
                key: "features",
                label: (
                  <div className={styles.features}>
                    <img alt="cta" className={styles.image} src={featuresCtaSrc} />
                    <div className={styles.header}>
                      <div className={styles.title}>
                        {intl.formatMessage(messages.featuresTitle)}
                      </div>
                      <div className={styles.description}>
                        {intl.formatMessage(messages.featuresDescription)}
                      </div>
                    </div>
                    <FontAwesomeIcon className={styles.icon} icon={faAngleRight} />
                  </div>
                ),
                onClick: handleFeaturesClick,
              },
            ]
          : []),
        ...(isOwner(me) && configService.getCurrentEnvironment() !== Environment.SANDBOX
          ? [
              {
                icon: <FontAwesomeIcon icon={faUsersCog} />,
                key: "manageSubscription",
                label: (
                  <Link onClick={() => navbarUserMenuClick("manageSubscription")} to={Path.BILLING}>
                    {intl.formatMessage(messages.subscriptionMenuItem)}
                  </Link>
                ),
              },
            ]
          : []),
        {
          icon: <FontAwesomeIcon icon={faLifeRing} />,
          key: "support",
          label: (
            <a
              href="https://hs.mapmycustomers.me/en/support"
              onClick={() => navbarUserMenuClick("support")}
              rel="noopener noreferrer"
              target="_blank"
            >
              {intl.formatMessage(messages.supportMenuItem)}
            </a>
          ),
        },
        { type: "divider" },
        {
          className: styles.logout,
          icon: <FontAwesomeIcon icon={faArrowLeftFromBracket} />,
          key: "logout",
          label: intl.formatMessage(messages.logoutMenuItem),
          onClick: handleLogoutClick,
        },
      ],
    };
  }, [handleFeaturesClick, handleLogoutClick, intl, me, showFeaturesBlock]);

  return (
    <Dropdown menu={menu} placement="bottomRight" trigger={["click"]}>
      <div className={styles.container} data-action-id="user-menu">
        <Avatar className={styles.avatar} tooltip={false} user={me} />
      </div>
    </Dropdown>
  );
};

const mapStateToProps = (state: RootState) => ({
  addOnServices: getOrganizationAddOnServices(state)!,
  currentPlan: getPlan(state),
  me: getMe(state)!, // profile must be loaded beforehand
  plans: getPlans(state),
});

const mapDispatchToProps = {
  logout,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserMenu);
